<template>
  <vl-grid v-vl-spacer:top.medium>
    <vl-column>
      <div class="pbs-form-group">
        <vl-grid mod-stacked>
          <vl-column width="2" width-s="6" width-xs="12">
            <vl-form-message-label for="id">{{ $t('productgroup.list.filter.id.label') }}</vl-form-message-label>
            <vl-input-field
              id="id"
              v-model="filter.id"
              name="id"
              type="number"
              :placeholder="$t('productgroup.list.filter.id.label')"
              mod-block
              @keyup.enter="doSearch"
            />
          </vl-column>
          <vl-column width="4" width-s="6" width-xs="12">
            <vl-form-message-label for="label">{{ $t('productgroup.list.filter.label.label') }}</vl-form-message-label>
            <vl-input-field
              id="label"
              v-model="filter.label"
              name="label"
              type="search"
              :placeholder="$t('productgroup.list.filter.label.label')"
              mod-block
              @keyup.enter="doSearch"
            />
          </vl-column>
          <vl-column width="6" width-s="6" width-xs="12">
            <vl-form-message-label for="productProcessDefinition">{{
              $t('productgroup.list.filter.productProcessDefinition.label')
            }}</vl-form-message-label>
            <PbsSelect
              id="productProcessDefinition"
              v-model="filter.productProcessDefinition"
              :options="productProcessDefinitionOptions"
              :placeholder="$t('productgroup.list.filter.productProcessDefinition.label')"
              :custom-label="getDefinitionLabel"
              :sortFunction="definitionSortFunction"
            />
          </vl-column>

          <vl-column v-vl-spacer:top.small width="9" width-s="9" width-xs="12">
            <vl-action-group>
              <vl-button @click="doSearch">{{ $t('productgroup.list.filter.search') }}</vl-button>
              <vl-button mod-secondary @click="clear">{{ $t('productgroup.list.filter.clear') }}</vl-button>
            </vl-action-group>
          </vl-column>
          <vl-column v-vl-spacer:top.small width="3" width-s="3" width-xs="12">
            <vl-action-group mod-align-right>
              <vl-button mod-secondary @click="create">{{ $t('productgroup.list.filter.create') }}</vl-button>
            </vl-action-group>
          </vl-column>
        </vl-grid>
      </div>
    </vl-column>
  </vl-grid>
  <vl-grid v-vl-spacer:top.medium>
    <vl-data-table-extended
      v-if="items"
      :data="items"
      :columns="columnMap"
      :fetching="loading"
      :meta="metaData"
      mod-clickable-rows
      mod-zebra
      @column-clicked="columnClicked"
      @pager-clicked="pagerClicked"
    >
      <template v-slot:noresults>
        <template v-if="loading">
          <vl-region>
            <div v-vl-align:center>
              <vl-loader :message="$t('general.pageLoading')" />
            </div>
          </vl-region>
        </template>
        <template v-else-if="isMounted">
          <div v-vl-align:left>
            <div class="vl-u-spacer--large">
              <vl-title tag-name="h2">{{ $t('productgroup.list.table.noResults.title') }}</vl-title>
              <p>{{ $t('productgroup.list.table.noResults.subtitle') }}</p>
            </div>
          </div>
        </template>
      </template>
    </vl-data-table-extended>
  </vl-grid>
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, Ref } from 'vue';
import { ProductGroupListFilter, useProductGroupListStore } from '../../store/product-group/product-group-list.store';
import { ColumnDefExtended } from '@/components/vl-data-table-extended/types';
import { RowData } from '@tanstack/vue-table';
import { ProductGroupSearchResultDto, ProductProcessDefinitionSearchResultDto } from '@/api/backoffice/clients';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlertStore, IAlert } from '@/modules/core/store/alert.store';
import { useProductProcessDefinitionListStore } from '../../store/product-process-definition/product-process-definition-list.store';
import PbsSelect from '@/components/forms/PbsSelect.vue';

const store = useProductGroupListStore();
const productProcessDefinitionStore = useProductProcessDefinitionListStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const isMounted = ref(false);
const productProcessDefinitionOptions: Ref<ProductProcessDefinitionSearchResultDto[]> = ref([]);

const filter = computed((): ProductGroupListFilter => {
  return store.filter ?? {};
});

const loading = computed((): boolean => {
  return store.loading;
});

const items = computed((): ProductGroupSearchResultDto & { routerLink?: string }[] => {
  return store.items?.map((i) => {
    return {
      ...i,
      routerLink: `/configuration/productgroup/${i.id}`
    };
  });
});

const metaData = computed(() => {
  return store.metaData;
});

const columnMap = computed((): ColumnDefExtended<RowData>[] => {
  return [
    {
      accessorKey: 'id',
      header: t('productgroup.list.table.header.id'),
      enableSorting: true,
      size: 5,
      link: true
    },
    {
      accessorKey: 'label',
      header: t('productgroup.list.table.header.label'),
      enableSorting: true,
      sortDescFirst: false,
      size: 30,
      link: true
    },
    {
      accessorKey: 'description',
      header: t('productgroup.list.table.header.description'),
      enableSorting: true,
      sortDescFirst: false,
      size: 45,
      link: true
    },
    {
      // Both counts are projections over the children, not columns on the product group,
      // so the backend cannot order on them.
      accessorKey: 'productProcessDefinitionCount',
      header: t('productgroup.list.table.header.productProcessDefinitionCount'),
      enableSorting: false,
      size: 10,
      link: true
    },
    {
      accessorKey: 'emailCount',
      header: t('productgroup.list.table.header.emailCount'),
      enableSorting: false,
      size: 10,
      link: true
    }
  ];
});

/*
  PbsSelect sorts the options itself, on the rendered label. That label starts with the id, so without
  a sort function of its own the list comes out ordered by id instead of by name.
*/
const definitionSortFunction = (a: ProductProcessDefinitionSearchResultDto, b: ProductProcessDefinitionSearchResultDto) => {
  return (a?.label ?? '').localeCompare(b?.label ?? '');
};

const getDefinitionLabel = (entry: ProductProcessDefinitionSearchResultDto) => {
  return `[${entry?.id}] ${entry?.label}`;
};

const alert = computed((): IAlert => {
  return useAlertStore().alert;
});

onMounted(() => {
  setTimeout(async () => {
    productProcessDefinitionOptions.value = await productProcessDefinitionStore.getAll();
    store.init(createFilterFromRoute());
    await search();
    isMounted.value = true;
  }, 0);
});

// All configuration tabs share one route, so a search that is still running when the user
// switches tab must not write its filter back into the query string afterwards.
let isActive = true;

onUnmounted(() => {
  isActive = false;
  store.clearFilter();
});

const clear = async () => {
  store.clearFilter();
  await search();
};

const doSearch = async () => {
  await search({ resetPager: true });
};

const search = async ({ resetPager }: { resetPager: boolean } = { resetPager: false }) => {
  if (resetPager) {
    store.resetPager();
  }

  await store.getProductGroups();
  if (isActive && !alert.value) {
    updateRoute();
  }
};

const create = () => {
  router.push({ name: 'productgroup.create' });
};

const createFilterFromRoute = (): ProductGroupListFilter => {
  const filter: ProductGroupListFilter = {};

  Object.keys(route.query).forEach((k) => {
    if (k === 'productProcessDefinition') {
      filter[k] = productProcessDefinitionOptions.value.find((x) => x.id === parseInt(route.query[k] as string));
    } else {
      filter[k] = route.query[k] as string | number | boolean;
    }
  });

  return filter;
};

const updateRoute = () => {
  const query = {};
  Object.keys(store.filter).forEach((k) => {
    if (k === 'productProcessDefinition') {
      query[k] = store.filter[k]?.id ? store.filter[k].id : '';
    } else {
      query[k] = store.filter[k];
    }
  });

  router.push({
    ...route,
    path: route.path,
    query
  });
};

const pagerClicked = (pageNumber: number) => {
  store.updatePage(pageNumber);
  search();
};

const columnClicked = (evt: { key: string; direction: string }) => {
  store.updateSorting(evt.key, evt.direction);
  search({ resetPager: true });
};
</script>
<style lang="scss" scoped>
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colorsRaw.scss';
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colors.scss';

.pbs-form-group {
  background: $vl-alt-bg;
  padding: 3rem;
}
.vl-grid {
  flex-grow: 1;
}
</style>
